import React, { useState, useMemo } from 'react';
import { Tab, Tabs } from '@mui/material';
import { Box } from '@mui/system';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { definedTranslation } from '../../../../pages/ProductSearch/i18n';
import { ProductSearchMaterial } from '../../../../pages/ProductSearch/types';
import { RecommendStripUI } from '../../../RecommendStripUI/RecommendStripUI';
import { getRecommendStrip } from '../../api/materials';
import useAddToBagWithValidations from '../../hooks/useAddToBagWithValidations';
import { recommendDataSelector } from '../../redux/selectors/recommendSelector';
import {
  setStripData,
  StripDataProps,
} from '../../redux/sliceCreators/recommendSlice';

interface RecommendBlockProps {
  activeItemId?: string;
  onRecommendationClick: (product: ProductSearchMaterial) => void;
}

export const RecommendBlock: React.FC<RecommendBlockProps> = ({
  onRecommendationClick,
  activeItemId,
}) => {
  const intl = useIntl();

  const materialsData = useSelector(recommendDataSelector);
  const [activeTab, setActiveTab] = useState(0);
  const { handleAddToBag: addToBag, loadingIds } = useAddToBagWithValidations();
  const dispatch = useDispatch();

  const handleTabChange = (_: any, value: number) => {
    const selectedTabData = materialsData?.[value];
    if (selectedTabData) {
      if (selectedTabData?.materials?.length === 0 && !selectedTabData.loaded) {
        getNewTabContent(selectedTabData);
      }
    }
    setActiveTab(value);
  };

  const getNewTabContent = async (selectedTabData: StripDataProps) => {
    const loadedMaterials = await getRecommendStrip({
      urlPath: selectedTabData.urlPath,
      payload: selectedTabData.payload,
    });
    const newRecommendData = materialsData?.map((item: StripDataProps) => {
      if (item.label === selectedTabData.label) {
        return { ...item, materials: loadedMaterials, loaded: true };
      } else {
        return { ...item, loaded: true };
      }
    });
    dispatch(setStripData(newRecommendData));
  };

  const handleAddToCart = (product: ProductSearchMaterial) => {
    addToBag({
      product,
      shouldTriggerRecModal: false,
      onAddToCartClick: undefined,
      qvLocation: product?.qvLocation || 'recommend',
      atcLocation: 'QV',
      gaEventLocation: `quick view recos ab b ${product?.analyticsPostfix}`,
      dataLayerAnalytics: {
        listId: `qvrecos_ab_b_${product?.analyticsPostfix}`,
        listName: `quick view recos ab b ${product?.analyticsPostfix}`,
        eventLocation: `quick view recos ab b ${product?.analyticsPostfix}`,
      },
    });
  };

  const handleFilterEmptyList = (item: StripDataProps) => {
    return item?.materials?.length > 0 || !item.loaded;
  };

  const cardLabels = useMemo(() => {
    return {
      addToCart:
        intl?.formatMessage(definedTranslation.card.addToCartLabel) ||
        'Add to Cart',
      learnMore:
        intl?.formatMessage(definedTranslation.card.learnMoreLabel) ||
        'Learn More',
      sampleFinishes:
        intl?.formatMessage(definedTranslation.card.sampleFinishesLabel) ||
        'Sample Finishes',
    };
  }, [intl]);

  const currentTabData = materialsData?.[activeTab];

  if (
    currentTabData == null ||
    (currentTabData?.materials?.length === 0 && currentTabData?.loaded)
  ) {
    return null;
  }

  return (
    <Box
      id="recommendBlockMiniPDP"
      display="flex"
      width="100%"
      flexDirection="column"
      position="relative"
    >
      <Tabs
        variant="scrollable"
        scrollButtons="auto"
        value={activeTab}
        onChange={handleTabChange}
        sx={{
          borderBottom: (theme) => `1px solid ${theme.palette.grey[50]}`,
          '& .MuiTab-root': {
            marginRight: '0.6em',
            padding: '8px',
            fontWeight: 600,
            fontSize: '20px',
            lineHeight: '22px',
            textTransform: 'none',
            color: (theme) => theme.palette.grey['A400'],
            '&.Mui-selected': {
              color: (theme) => `${theme.palette.primary.main} !important`,
            },
          },
          '& .MuiTabScrollButton-root.Mui-disabled': {
            opacity: '0.25 !important',
          },
        }}
      >
        {materialsData
          ?.filter(handleFilterEmptyList)
          ?.map(({ label }: StripDataProps, index: number) => (
            <Tab
              disableRipple
              key={label}
              label={label}
              value={index}
              sx={tabSx}
            />
          ))}
      </Tabs>
      <RecommendStripUI
        showSecondaryActionButton
        cartLoadingIds={loadingIds}
        materials={currentTabData?.materials}
        onAddToCart={handleAddToCart}
        onCardClick={onRecommendationClick}
        activeItemId={activeItemId}
        cardButtonsLabels={cardLabels}
      />
    </Box>
  );
};

//////
const tabSx = {
  maxWidth: '600px',
};
